import React from 'react'
import styled from 'react-emotion'
import Toolbar from '../../common/Toolbar'
import {
  border,
  borderRadius,
  space,
  color,
  discussBlockHighlight
} from '../../../style'
import { StyledFormElement as FormElement } from '../../common/FormElement'
import Comment from '../Comment'
import CommentForm from '../CommentForm'
import Text from 'common/components/Text'
import { withState, compose, mapProps, lifecycle } from 'recompose'

const ThreadToolbar = ({
  read,
  dateResolved,
  markRead,
  markUnread,
  resolveThread,
  unresolveThread,
  anon,
  t
}) =>
  dateResolved ? (
    // Resolved Thread
    <Toolbar top borderless>
      {/* Date Resolved */}
      <FormElement>
        <Text small medium light>
          {t('discuss.thread.isResolved', { date: dateResolved })}
        </Text>
      </FormElement>

      <Toolbar.Spacer />

      {/* Restore */}
      {!anon && (
        <FormElement>
          <Text small medium light onClick={unresolveThread}>
            {t('discuss.thread.unresolve')}
          </Text>
        </FormElement>
      )}
    </Toolbar>
  ) : (
    // Normal Thread
    <Toolbar top borderless>
      {read ? (
        //  Mark unread
        <FormElement>
          <Text small medium light onClick={markUnread}>
            {t('discuss.thread.markUnread')}
          </Text>
        </FormElement>
      ) : (
        // Mark Read
        <FormElement>
          <Text small medium light onClick={markRead}>
            {t('discuss.thread.markRead')}
          </Text>
        </FormElement>
      )}

      <Toolbar.Spacer />

      {/* Resolve */}
      {!anon && (
        <FormElement>
          <Text small medium light onClick={resolveThread}>
            {t('discuss.thread.resolve')}
          </Text>
        </FormElement>
      )}
    </Toolbar>
  )

const Thread = styled(
  React.forwardRef(
    (
      {
        thread,
        comments,
        preview,
        markRead,
        markUnread,
        resolveThread,
        unresolveThread,
        placement,
        anon,
        className,
        t
      },
      ref
    ) => (
      <div className={className} ref={ref}>
        {!thread.new && !preview && (
          <ThreadToolbar
            read={thread.read}
            dateResolved={thread.dateResolved}
            markRead={markRead}
            markUnread={markUnread}
            resolveThread={resolveThread}
            unresolveThread={unresolveThread}
            anon={anon}
            t={t}
          />
        )}

        {!thread.new && !preview && (
          <ScrollArea>
            {comments.map(comment => (
              <Comment comment={comment} thread={thread} key={comment.id} />
            ))}
          </ScrollArea>
        )}
        {preview && comments && comments.length && (
          <Comment
            preview={{ replies: comments.length - 1 }}
            comment={comments[0]}
            thread={thread}
          />
        )}

        {!preview && !thread.dateResolved && (
          <CommentForm thread={thread} autoFocus={!!placement} /> // Don't focus until after placement or bad scroll will happen
        )}
      </div>
    )
  )
)(({ style, thread, placement }) => ({
  label: 'Thread',
  width: 300,
  background: 'white',
  marginTop:
    thread.blockId &&
    (placement === 'right-start' || placement === 'left-start') &&
    -discussBlockHighlight.outlineWidth,
  marginRight:
    placement === 'bottom-end-start' && -discussBlockHighlight.outlineWidth,
  border,
  borderStyle: thread.dateResolved && 'dashed',
  borderColor: !thread.new && !thread.read && color.black,
  borderRadius,
  overflow: 'hidden', // stops buttons poking out from rounded corners

  maxHeight: `calc(100vh - ${space.large * 4}px)`,
  display: 'flex',
  flexDirection: 'column',

  ...style,

  [Toolbar]: { paddingRight: 5, paddingLeft: 5 } // in here things are a 5px further from the edge than other toolbars?
}))

const scrollToBottom = compose(
  withState('scrollRef', 'innerRef'),
  lifecycle({
    componentDidUpdate(oldProps) {
      if (
        this.props.children.length > oldProps.children.length ||
        (this.props.scrollRef && !oldProps.scrollRef)
      )
        this.props.scrollRef.scrollTop = this.props.scrollRef.scrollHeight
    }
  }),
  mapProps(({ innerRef, children }) => ({ innerRef, children }))
)
const ScrollArea = scrollToBottom(styled('div')({ overflow: 'auto' }))

export default Thread
